import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus, 
  Folder, 
  ChartLine, 
  SignOut, 
  DotsThree,
  Scan,
  Trash,
  PencilSimple,
  QrCode
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, useAuth } from "@/App";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDesc, setNewProjectDesc] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, overviewRes] = await Promise.all([
        api.get("/projects"),
        api.get("/analytics/overview"),
      ]);
      setProjects(projectsRes.data);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error("El nombre del proyecto es requerido");
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post("/projects", {
        name: newProjectName,
        description: newProjectDesc,
      });
      setProjects([response.data, ...projects]);
      setShowNewProject(false);
      setNewProjectName("");
      setNewProjectDesc("");
      toast.success("Proyecto creado");
    } catch (error) {
      toast.error("Error al crear el proyecto");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("¿Eliminar este proyecto y todos sus códigos QR?")) return;
    
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects(projects.filter((p) => p.project_id !== projectId));
      toast.success("Proyecto eliminado");
    } catch (error) {
      toast.error("Error al eliminar el proyecto");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05152E' }}>
        <div className="w-12 h-12 border-4 border-t-transparent animate-spin" style={{ borderColor: '#008AD8', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dark-dashboard" style={{ backgroundColor: '#05152E' }} data-testid="dashboard">
      {/* Header */}
      <header className="border-b sticky top-0 z-50" style={{ backgroundColor: '#05152E', borderColor: '#1A3A5C' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/images/logo-dark.png" 
              alt="CauraTech" 
              className="h-14 w-auto"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <button
              data-testid="analytics-nav-btn"
              onClick={() => navigate("/analytics")}
              className="flex items-center gap-2 px-4 py-2 transition-colors"
              style={{ color: '#A1A1AA' }}
              onMouseEnter={(e) => e.target.style.color = '#008AD8'}
              onMouseLeave={(e) => e.target.style.color = '#A1A1AA'}
            >
              <ChartLine size={20} weight="bold" />
              <span className="hidden sm:inline">Analíticas</span>
            </button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-4 py-2 border transition-colors" style={{ borderColor: '#1A3A5C' }}>
                  <img 
                    src={user?.picture || "https://via.placeholder.com/32"} 
                    alt={user?.name}
                    className="w-6 h-6 rounded-full"
                  />
                  <span className="hidden sm:inline text-sm font-medium text-white">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-none border" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="rounded-none cursor-pointer hover:bg-[#0D2847]"
                  style={{ color: '#A1A1AA' }}
                >
                  <SignOut size={16} className="mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border" style={{ borderColor: '#1A3A5C' }}>
          {[
            { label: "Total Códigos QR", value: overview?.total_qrcodes || 0, icon: QrCode, accent: true },
            { label: "Total Proyectos", value: overview?.total_projects || 0, icon: Folder },
            { label: "Total Escaneos", value: overview?.total_scans || 0, icon: Scan },
            { label: "Últimos 7 Días", value: overview?.recent_scans || 0, icon: ChartLine },
          ].map((stat, index) => (
            <div 
              key={index} 
              className={`p-6 ${index < 3 ? 'border-r' : ''} ${index < 2 ? 'border-b lg:border-b-0' : ''}`}
              style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}
            >
              <stat.icon size={24} weight="bold" className="mb-3" style={{ color: stat.accent ? '#008AD8' : '#71717A' }} />
              <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>{stat.label}</p>
              <p 
                className="text-3xl font-bold"
                style={{ fontFamily: 'IBM Plex Mono, monospace', color: stat.accent ? '#008AD8' : 'white' }}
              >
                {stat.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* Projects Section */}
        <div className="flex items-center justify-between mb-6 mt-8">
          <div>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Tu Espacio de Trabajo</p>
            <h2 
              className="text-2xl font-black text-white"
            >
              PROYECTOS
            </h2>
          </div>
          <button
            data-testid="new-project-btn"
            onClick={() => setShowNewProject(true)}
            className="flex items-center gap-2 text-sm px-6 py-3 bg-white font-semibold border-2 border-white hover:bg-[#F4F4F5] transition-colors"
            style={{ color: '#003594', boxShadow: '4px 4px 0px 0px #008AD8' }}
          >
            <Plus size={18} weight="bold" />
            Nuevo Proyecto
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="border border-dashed p-12 text-center" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
            <Folder size={48} weight="duotone" className="mx-auto mb-4" style={{ color: '#1A3A5C' }} />
            <h3 className="font-bold text-lg text-white mb-2">
              Sin proyectos aún
            </h3>
            <p className="mb-4" style={{ color: '#71717A' }}>
              Crea tu primer proyecto para comenzar a generar códigos QR
            </p>
            <button
              onClick={() => setShowNewProject(true)}
              className="px-6 py-3 bg-transparent text-white font-semibold border transition-colors"
              style={{ borderColor: '#1A3A5C' }}
            >
              Crear Proyecto
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border" style={{ borderColor: '#1A3A5C' }}>
            {projects.map((project, index) => (
              <div 
                key={project.project_id}
                data-testid={`project-card-${project.project_id}`}
                className={`p-6 cursor-pointer hover:bg-[#0D2847] transition-colors ${
                  index % 3 !== 2 ? 'lg:border-r' : ''
                } ${index < projects.length - 3 ? 'border-b' : ''}`}
                style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}
                onClick={() => navigate(`/projects/${project.project_id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#0D2847' }}>
                    <Folder size={20} weight="bold" style={{ color: '#A1A1AA' }} />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button className="p-2 hover:bg-[#0D2847] transition-colors" style={{ color: '#A1A1AA' }}>
                        <DotsThree size={20} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-none border" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.project_id}`);
                        }}
                        className="rounded-none cursor-pointer hover:bg-[#0D2847]"
                        style={{ color: '#A1A1AA' }}
                      >
                        <PencilSimple size={16} className="mr-2" />
                        Abrir
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.project_id);
                        }}
                        className="rounded-none cursor-pointer hover:bg-[#0D2847] text-red-400"
                      >
                        <Trash size={16} className="mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <h3 className="font-bold text-lg text-white mb-1">
                  {project.name}
                </h3>
                {project.description && (
                  <p className="text-sm mb-4 line-clamp-2" style={{ color: '#71717A' }}>
                    {project.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: '#1A3A5C' }}>
                  <div>
                    <p className="text-xs" style={{ color: '#71717A' }}>Códigos QR</p>
                    <p className="font-bold text-white" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {project.qr_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#71717A' }}>Escaneos</p>
                    <p className="font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#008AD8' }}>
                      {project.total_scans || 0}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="rounded-none border sm:max-w-md" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
          <DialogHeader>
            <DialogTitle 
              className="text-xl font-black text-white"
            >
              Crear Nuevo Proyecto
            </DialogTitle>
            <DialogDescription style={{ color: '#71717A' }}>
              Organiza tus códigos QR en proyectos para una mejor gestión.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-2" style={{ color: '#71717A' }}>Nombre del Proyecto</label>
              <input
                data-testid="project-name-input"
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="ej: Campaña Verano 2026"
                className="w-full px-4 py-3 border bg-[#0D2847] text-white focus:outline-none transition-colors placeholder:text-[#71717A]"
                style={{ borderColor: '#1A3A5C' }}
              />
            </div>
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-2" style={{ color: '#71717A' }}>Descripción (Opcional)</label>
              <textarea
                data-testid="project-desc-input"
                value={newProjectDesc}
                onChange={(e) => setNewProjectDesc(e.target.value)}
                placeholder="Breve descripción del proyecto..."
                className="w-full px-4 py-3 border bg-[#0D2847] text-white focus:outline-none transition-colors min-h-[80px] resize-none placeholder:text-[#71717A]"
                style={{ borderColor: '#1A3A5C' }}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <button
              onClick={() => setShowNewProject(false)}
              className="px-4 py-2 border transition-colors"
              style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
            >
              Cancelar
            </button>
            <button
              data-testid="create-project-submit-btn"
              onClick={handleCreateProject}
              disabled={creating}
              className="px-6 py-2 bg-white font-semibold hover:bg-[#F4F4F5] transition-colors"
              style={{ color: '#003594' }}
            >
              {creating ? "Creando..." : "Crear Proyecto"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
